import React from 'react';
import useGameStore from '../../store/gameStore';
import { Trophy, MapPin, Truck, Globe, Sparkles, ArrowRight } from 'lucide-react';
import { useTranslation } from '../../i18n';

const PHASE_INFO = {
  2: {
    titleKey: 'phase_2_title',
    subtitleKey: 'phase_2_subtitle',
    icon: MapPin,
    color: 'from-blue-600 to-blue-800',
    descriptionKey: 'phase_2_description',
    unlockKeys: [
      'phase_2_unlock_1',
      'phase_2_unlock_2',
      'phase_2_unlock_3',
      'phase_2_unlock_4',
    ],
    conditionKey: 'phase_2_condition',
  },
  3: {
    titleKey: 'phase_3_title',
    subtitleKey: 'phase_3_subtitle',
    icon: Truck,
    color: 'from-indigo-600 to-indigo-900',
    descriptionKey: 'phase_3_description',
    unlockKeys: [
      'phase_3_unlock_1',
      'phase_3_unlock_2',
      'phase_3_unlock_3',
      'phase_3_unlock_4',
    ],
    conditionKey: 'phase_3_condition',
  },
  4: {
    titleKey: 'phase_4_title',
    subtitleKey: 'phase_4_subtitle',
    icon: Globe,
    color: 'from-violet-600 to-violet-900',
    descriptionKey: 'phase_4_description',
    unlockKeys: [
      'phase_4_unlock_1',
      'phase_4_unlock_2',
      'phase_4_unlock_3',
      'phase_4_unlock_4',
    ],
    conditionKey: 'phase_4_condition',
  },
};

export const PhaseNotification = () => {
  const { t } = useTranslation();
  const { pendingPhaseUp, dismissPhaseUp } = useGameStore();

  if (!pendingPhaseUp) return null;

  const info = PHASE_INFO[pendingPhaseUp];
  if (!info) return null;

  const Icon = info.icon;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="phase-notification-overlay">
      <div className="bg-ink-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500" data-testid="phase-notification">
        {/* Gradient Header */}
        <div className={`bg-gradient-to-br ${info.color} px-6 py-8 text-center relative overflow-hidden`}>
          {/* Sparkle decorations */}
          <div className="absolute top-3 left-6 opacity-40">
            <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
          </div>
          <div className="absolute top-8 right-8 opacity-30">
            <Sparkles className="w-4 h-4 text-yellow-200 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
          <div className="absolute bottom-4 left-12 opacity-20">
            <Sparkles className="w-6 h-6 text-yellow-300 animate-pulse" style={{ animationDelay: '1s' }} />
          </div>

          <div className="w-16 h-16 mx-auto bg-ink-800/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
            <Icon className="w-8 h-8 text-foreground" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-yellow-300" />
            <p className="text-yellow-200 text-sm font-semibold uppercase tracking-wider">{t('phase_new_phase_label')}</p>
            <Trophy className="w-5 h-5 text-yellow-300" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">{t(info.titleKey)}</h2>
          <p className="text-foreground/80 text-sm mt-1">{t(info.subtitleKey)}</p>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-slate-400 leading-relaxed mb-4">{t(info.descriptionKey)}</p>

          <div className="bg-ink-700/30 rounded-lg p-4">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-3">{t('new_unlocks')}</h4>
            <div className="space-y-2">
              {info.unlockKeys.map((uKey) => (
                <div key={uKey} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-money/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ArrowRight className="w-3 h-3 text-money" />
                  </div>
                  <p className="text-sm text-slate-300">{t(uKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={dismissPhaseUp}
            className="w-full bg-accent text-white py-3 rounded-xl text-sm font-bold hover:bg-accent-hover transition-colors active:scale-[0.98]"
            data-testid="dismiss-phase-btn"
          >
            {t('lets_go')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhaseNotification;
